import { useState, useEffect, useCallback } from "react";
import { Grid, TextField, Box, Card, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useApi } from "@platform/api";
import { Alert, Button, Typography } from "@platform/design-system";
import { useUser } from "@platform/user/client";

import { extractErrorMessage } from "../../utils";
import { Loader } from "../../components";

import UpdatePassword from "./updatePassword";

const GIVEN_NAME = "givenName";
const SURNAME = "surname";
const MOBILE_PHONE = "mobilePhone";
const DISPLAY_NAME = "displayName";
const defaultValues = [GIVEN_NAME, SURNAME, MOBILE_PHONE].reduce((acc, f) => {
  acc[f] = "";
  return acc;
}, {});

function Profile() {
  const theme = useTheme();
  const axiosInstance = useApi();
  const { updateName } = useUser();
  const [state, setState] = useState({
    state: "init",
    data: defaultValues,
    originalData: defaultValues,
    errors: defaultValues,
    error: null,
    stateMessage: null,
    updatePassword: false,
  });

  const updateState = useCallback(function updateState(newState) {
    setState((s) => ({ ...s, ...newState }));
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        updateState({ state: "loading" });
        const { data: responseData } = await axiosInstance.post(
          "brokerportal/v1/userProfile",
        );
        const data = {
          [GIVEN_NAME]: responseData[GIVEN_NAME],
          [SURNAME]: responseData[SURNAME],
          [MOBILE_PHONE]: responseData[MOBILE_PHONE],
          avatarLetter: responseData.displayName.trim().charAt(0).toUpperCase(),
        };
        updateState({
          state: "success",
          originalData: data,
          data,
        });
      } catch (error) {
        updateState({
          state: "error",
          stateMessage: extractErrorMessage(
            error,
            "There was an issue retrieving your user information",
          ),
        });
      }
    };
    fetchUserProfile();
  }, [axiosInstance, updateState]);

  function handleChange({ target }) {
    const { name, value } = target;
    updateState({
      data: { ...state.data, [name]: value },
      errors: { ...state.errors, [name]: "" },
    });
  }

  function handleBlur({ target }) {
    const { name, value } = target;
    updateState({
      errors: { ...state.errors, [name]: value ? "" : "No empty value" },
    });
  }

  function validateForm() {
    const errors = [GIVEN_NAME, SURNAME].reduce((acc, f) => {
      if (!state.data[f]) {
        acc[f] = "No empty value";
      }
      return acc;
    }, {});
    if (Object.values(errors).length > 0) {
      updateState({ errors });
      return false;
    }
    return true;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const data = {
        [GIVEN_NAME]: state.data[GIVEN_NAME],
        [SURNAME]: state.data[SURNAME],
        [MOBILE_PHONE]: state.data[MOBILE_PHONE],
        [DISPLAY_NAME]: `${state.data[GIVEN_NAME]} ${state.data[SURNAME]}`,
      };
      updateState({ state: "loading", errors: defaultValues });
      await axiosInstance.post("brokerportal/v1/updateUserProfile", data, {
        headers: { "Content-Type": "application/json" },
      });
      updateState({
        state: "success",
        stateMessage: "Profile save!",
        originalData: state.data,
      });
      updateName({
        givenName: state.data[GIVEN_NAME],
        familyName: state.data[SURNAME],
      });
    } catch (err) {
      updateState({
        state: "error",
        stateMessage: extractErrorMessage(err, "Error updating user profile"),
      });
    }
  }

  const hasErrors = useMemo(
    () => Object.values(state.errors).some((e) => !!e),
    [state.errors],
  );

  if (state.state === "loading") {
    return <Loader />;
  }

  if (state.updatePassword) {
    return (
      <UpdatePassword
        handleGoBack={() => updateState({ updatePassword: false })}
      />
    );
  }

  return (
    <Box maxWidth={theme.breakpoints.values.sm}>
      {state.stateMessage ? (
        <Box mb={2}>
          <Alert
            severity={state.state}
            onClose={() => updateState({ stateMessage: null })}
          >
            {state.stateMessage}
          </Alert>
        </Box>
      ) : null}
      <Typography variant="display-medium" marginBottom={5}>
        Profile
      </Typography>
      <Card>
        <Box
          component="form"
          display="flex"
          flexDirection="column"
          alignItems="center"
          onSubmit={handleSubmit}
          px={3}
        >
          <Box width="100%" p={3}>
            <Typography variant="header-large">Personal Information</Typography>
            <Box mb={5} mt={3} display="flex" flexDirection="column" gap={2}>
              <Grid container spacing={3}>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    value={state.data[GIVEN_NAME]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    name={GIVEN_NAME}
                    error={!!state.errors[GIVEN_NAME]}
                    helperText={state.errors[GIVEN_NAME]}
                    label={"First name"}
                    autoComplete="none"
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    value={state.data[SURNAME]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    name={SURNAME}
                    error={!!state.errors[SURNAME]}
                    helperText={state.errors[SURNAME]}
                    label={"Last name"}
                    autoComplete="none"
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    value={state.data[MOBILE_PHONE]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    name={MOBILE_PHONE}
                    error={!!state.errors[MOBILE_PHONE]}
                    helperText={state.errors[MOBILE_PHONE]}
                    label={"Phone number"}
                    autoComplete="none"
                  />
                </Grid>
                <Grid size={12}>
                  <Button
                    fullWidth
                    size="lg"
                    variant="secondary"
                    onClick={() => updateState({ updatePassword: true })}
                  >
                    Update Password
                  </Button>
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Button variant="primary" type="submit" disabled={hasErrors}>
                Save Profile
              </Button>
              <Button
                variant="tertiary"
                type="button"
                onClick={() => updateState({ data: state.originalData })}
              >
                Undo Changes
              </Button>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

export default Profile;
